import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {Box, Stack, SxProps, Theme} from "@mui/material";
import Typography from "@mui/material/Typography";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {RunnerQuestionProps} from "./runnerQuestion.types";
import {choiceSurfaceSx} from "./runnerSurface";
import {surveyControls} from "../surveyRunnerColors";

const GLYPH_SIZE = 22;

function ChecklistQuestion({question, localize, language, value, onAnswerChange}: RunnerQuestionProps) {
    const {t} = useTranslation(undefined, {lng: language});

    const [localTicked, setLocalTicked] = useState<string[]>([]);

    const ticked = value?.optionIds ?? localTicked;

    const toggle = (optionId: string) => {
        const next = ticked.includes(optionId)
            ? ticked.filter(entry => entry !== optionId)
            : [...ticked, optionId];

        setLocalTicked(next);
        onAnswerChange?.({...value, optionIds: next});
    };

    /*
        The same card the choice questions use, not a column of native checkboxes: a checklist sits
        between a multiple choice and a yes/no in the same form, and a tick box a third of the size
        with no tap target around it read as a different product. Only the glyph differs - a check
        box says "as many as you like" where the choice card's circle says "one of these".
    */
    const cardSx = (active: boolean): SxProps<Theme> => (theme: Theme) => ({
        ...choiceSurfaceSx(active)(theme),
        width: '100%',
        px: 2,
        py: 1.25,
        textAlign: 'left',
        font: 'inherit',
        color: 'text.primary',
        cursor: 'pointer',
    });

    return (
        <Stack gap={1}>
            {/*
                A required checklist is only answered once EVERY item is ticked (see the runner's
                validation) - telling that guest to pick what applies would be an invitation to
                fail the very next step, so the hint states which of the two lists this is.
            */}
            <Typography variant={'body2'} color={'text.secondary'}>
                {question.isRequired ? t('Please confirm every item') : t('Select all that apply')}
            </Typography>

            <Stack
                gap={1}
                role={'group'}
                aria-label={surveyMarkdownToPlainText(localize(question.title)) || undefined}
            >
                {(question.options ?? []).map((option, index) => {
                    const key = option.id ?? `option-${index}`;
                    const active = ticked.includes(key);

                    return (
                        <Box
                            key={key}
                            component={'button'}
                            type={'button'}
                            role={'checkbox'}
                            aria-checked={active}
                            onClick={() => toggle(key)}
                            sx={cardSx(active)}
                        >
                            <Stack direction={'row'} alignItems={'center'} gap={1.5} sx={{minWidth: 0}}>
                                <Box
                                    aria-hidden
                                    sx={{
                                        display: 'flex',
                                        flexShrink: 0,
                                        color: theme => active ? surveyControls(theme).accent : surveyControls(theme).border,
                                    }}
                                >
                                    {active
                                        ? <CheckBoxIcon sx={{fontSize: GLYPH_SIZE}}/>
                                        : <CheckBoxOutlineBlankIcon sx={{fontSize: GLYPH_SIZE}}/>}
                                </Box>

                                <Typography variant={'body1'} sx={{minWidth: 0, wordBreak: 'break-word'}}>
                                    {localize(option.label)}
                                </Typography>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Stack>
    );
}

export default ChecklistQuestion;
